import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assignableRoles } from '@/application/assistants/assignableRoles';
import { looksLikePersonName, normalizePersonName } from '@/application/assistants/personName';
import { alignClinicIdsToScope, sameClinicIds } from '@/application/assistants/clinicIds';
import type { Assistant } from '@/application/assistants/assistantsTypes';
import type { Role } from '@/application/permissions/permissionsTypes';
import { ClinicsPicker } from '@/presentation/components/assistants/ClinicsPicker';
import { ModalPortal } from '@/presentation/components/common/ModalPortal';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';
import { useVisualViewportBottomInset } from '@/presentation/hooks/useVisualViewportBottomInset';

export function EditAssistantModal({
  assistant,
  ownerClinics,
  roles,
  nameFor,
  onRename,
  onSaveClinics,
  onAssignRole,
  onClose,
}: {
  assistant: Assistant;
  ownerClinics: string[];
  roles: Role[];
  nameFor?: (clinicId: string) => string;
  onRename: (id: string, nombre: string) => Promise<void>;
  onSaveClinics: (id: string, clinicaIds: string[]) => Promise<void>;
  onAssignRole: (id: string, panelRoleId: string | null) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(assistant.nombre);
  const [clinics, setClinics] = useState<string[]>(() => alignClinicIdsToScope(assistant.clinicaIds, ownerClinics));
  const [roleId, setRoleId] = useState(assistant.panelRoleId ?? '');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const backdropPressRef = useRef(false);
  const keyboardInset = useVisualViewportBottomInset();

  const dirty =
    normalizePersonName(name) !== assistant.nombre ||
    !sameClinicIds(clinics, assistant.clinicaIds) ||
    (roleId || null) !== assistant.panelRoleId;

  const requestClose = useCallback(() => {
    if (busy) return;
    if (dirty && !confirmingClose) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }, [busy, confirmingClose, dirty, onClose]);

  useKeyEsc(requestClose);

  const save = async () => {
    const nextName = normalizePersonName(name);
    setName(nextName);
    if (!looksLikePersonName(nextName)) {
      setFormError(t('assistants.wizard.nameInvalid'));
      return;
    }

    setFormError(null);
    setBusy(true);
    try {
      if (nextName !== assistant.nombre) {
        await onRename(assistant.id, nextName);
      }
      if (!sameClinicIds(clinics, assistant.clinicaIds)) {
        await onSaveClinics(assistant.id, clinics);
      }
      const nextRole = roleId || null;
      if (nextRole !== assistant.panelRoleId) {
        await onAssignRole(assistant.id, nextRole);
      }
      onClose();
    } catch {
      // El error ya se muestra a nivel de página.
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      role="presentation"
      data-testid="assistants-edit-backdrop"
      onPointerDown={(event) => {
        event.stopPropagation();
        backdropPressRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        event.stopPropagation();
        const startedOnBackdrop = backdropPressRef.current;
        backdropPressRef.current = false;
        if (startedOnBackdrop && event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-assistant-title"
        data-testid="assistants-edit-panel"
        className="flex min-h-0 max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4 dark:border-gray-700">
          <h2 id="edit-assistant-title" className="font-bold text-gray-900 dark:text-white">
            {t('assistants.edit.title')}
          </h2>
          <p className="mt-0.5 text-xs font-medium text-teal-700 dark:text-teal-300">{assistant.email}</p>
        </div>

        <div className="space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          {confirmingClose ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('assistants.edit.discardTitle')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('assistants.edit.discardMessage')}</p>
            </div>
          ) : (
            <>
              {formError && (
                <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
                  {formError}
                </p>
              )}
              <div>
                <label htmlFor="edit-assistant-nombre" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('assistants.new.nombre')}
                </label>
                <input
                  id="edit-assistant-nombre"
                  value={name}
                  autoFocus
                  onChange={(event) => {
                    setName(event.target.value);
                    setFormError(null);
                  }}
                  onBlur={() => setName(normalizePersonName(name))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                />
              </div>
              <div>
                <label htmlFor="edit-assistant-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('assistants.new.email')}
                </label>
                <input
                  id="edit-assistant-email"
                  value={assistant.email}
                  readOnly
                  className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('assistants.edit.emailLocked')}</p>
              </div>
              <div>
                <label htmlFor="edit-assistant-role" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('assistants.role.label')}
                </label>
                <select
                  id="edit-assistant-role"
                  aria-label={t('assistants.role.label')}
                  value={roleId}
                  disabled={busy}
                  onChange={(event) => setRoleId(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                >
                  <option value="">{t('assistants.role.none')}</option>
                  {assignableRoles(roles, assistant.panelRoleId).map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.isActive ? role.name : t('assistants.role.inactiveOption', { name: role.name })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">{t('assistants.new.clinics')}</p>
                <ClinicsPicker
                  clinics={ownerClinics}
                  selected={clinics}
                  onChange={setClinics}
                  disabled={busy}
                  idPrefix={`edit-clinics-${assistant.id}`}
                  nameFor={nameFor}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:justify-end sm:px-5 sm:py-4 dark:border-gray-700">
          {confirmingClose ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmingClose(false)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 sm:w-auto dark:border-gray-600 dark:text-gray-200"
              >
                {t('assistants.wizard.discardKeep')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto"
              >
                {t('assistants.wizard.discardConfirm')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={requestClose}
                disabled={busy}
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto"
              >
                {t('assistants.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy}
                className="w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-teal-600 sm:w-auto"
              >
                {busy ? t('common.saving') : t('assistants.actions.save')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
